// Segment parsing helpers for incremental parser path.
// Fallback keeps behavior identical with strict full-parser mode.
import type { LuisArrayVariables, LuisNode, LuisObjectVariables } from '../../types'
import { createStableHash } from '../../incremental/hash'
import { parseLuis } from '../../parser'
import type { LuisIncrementalParserOutput, LuisScannedSegment } from './types'
import { cloneLuisNode } from './tree'

// Parse one scanned segment into declarations/children payload.
export function parseOneSegment(
  segment: LuisScannedSegment,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables = {},
): { declarations: string[]; children: LuisNode[] } {
  // Parse top-level at-rule declaration segments directly.
  if (segment.kind === 'declaration') {
    const trimmed = segment.content.trim()
    // Keep only top-level at-rule declarations.
    if (trimmed.startsWith('@')) {
      // Return one declaration payload for root output.
      return { declarations: [trimmed], children: [] }
    } else {
      // Return empty payload for ignored declarations.
      return { declarations: [], children: [] }
    }
  } else {
    // Continue block segment parsing with full parser.
  }

  const parsed = parseLuis(segment.content, objectVariables, segment.line, arrayVariables)
  // Return parsed block children and declarations.
  return {
    declarations: [...parsed.declarations],
    children: parsed.children.map((child) => cloneLuisNode(child)),
  }
}

// Build fallback output by parsing full source in strict mode.
export function buildFallbackParseOutput(
  source: string,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables,
  objectSignature: string,
): LuisIncrementalParserOutput {
  const parsedRoot = parseLuis(source, objectVariables, 1, arrayVariables)
  // Return full parse output with single-cache-segment snapshot.
  return {
    root: parsedRoot,
    cache: {
      objectSignature,
      segments: [{
        key: 'fallback:0',
        kind: 'block',
        fingerprint: createStableHash(source),
        declarations: [...parsedRoot.declarations],
        children: parsedRoot.children.map((child) => cloneLuisNode(child)),
      }],
    },
    stats: {
      totalSegments: 1,
      reusedSegments: 0,
      parsedSegments: 1,
    },
  }
}
